import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import {
  BarChart3,
  Flag,
  Gem,
  type LucideProps,
  Mail,
  Palette,
  Plug,
  ScrollText,
  SlidersHorizontal,
  Type,
  Users,
} from "@fleet/icons";
import type { Component } from "solid-js";
import { Card, CardContent, Skeleton } from "@fleet/ui";
import { api, createApiQuery } from "@/lib/api";
import { type AdminCapability, adminQueryKeys, type components } from "@cashsync/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ErrorModel = components["schemas"]["ErrorModel"];
type AdminMetrics = components["schemas"]["AdminMetrics"];

interface Section {
  // Loose path: the `/u/<n>` prefix is injected by the `Link` wrapper at runtime.
  to: string;
  label: string;
  description: string;
  icon: Component<LucideProps>;
  capability: AdminCapability;
}

export function AdminOverview() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();

  const metricsQuery = createApiQuery<AdminMetrics, ErrorModel>(
    () => adminQueryKeys.metrics(),
    async () => api.GET("/api/admin/metrics", {}),
    () => ({ staleTime: 30_000, enabled: can("metrics.read") }),
  );

  const sections = (): Section[] => {
    const all: Section[] = [
      {
        to: "/admin/metrics",
        label: t(msg`Metrics`),
        description: t(msg`System health snapshot`),
        icon: BarChart3,
        capability: "metrics.read",
      },
      {
        to: "/admin/users",
        label: t(msg`Users`),
        description: t(msg`Roles, bans, sessions`),
        icon: Users,
        capability: "users.read",
      },
      {
        to: "/admin/audit-log",
        label: t(msg`Audit Log`),
        description: t(msg`Administrative actions`),
        icon: ScrollText,
        capability: "audit.read",
      },
      {
        to: "/admin/flags",
        label: t(msg`Feature Flags`),
        description: t(msg`Runtime toggles`),
        icon: Flag,
        capability: "flags.read",
      },
      {
        to: "/admin/monetization",
        label: t(msg`Monetization`),
        description: t(msg`Tiers, quotas & payments`),
        icon: Gem,
        capability: "billing.read",
      },
      {
        to: "/admin/config",
        label: t(msg`Operational config`),
        description: t(msg`Limits, retention & behaviour`),
        icon: SlidersHorizontal,
        capability: "settings.read",
      },
      {
        to: "/admin/integrations",
        label: t(msg`Integrations`),
        description: t(msg`Provider health`),
        icon: Plug,
        capability: "integrations.read",
      },
      {
        to: "/admin/fonts",
        label: t(msg`Fonts`),
        description: t(msg`Font catalog`),
        icon: Type,
        capability: "admin.access",
      },
      {
        to: "/admin/themes",
        label: t(msg`Themes`),
        description: t(msg`Theme catalog`),
        icon: Palette,
        capability: "admin.access",
      },
      {
        to: "/admin/email",
        label: t(msg`Email`),
        description: t(msg`Templates & campaigns`),
        icon: Mail,
        capability: "admin.access",
      },
    ];
    return all.filter((s) => can(s.capability));
  };

  return (
    <div class="space-y-8">
      <div>
        <h1 class="text-3xl font-bold tracking-tight">{t(msg`Admin`)}</h1>
        <p class="text-sm text-muted-foreground">{t(msg`Back-office control panel.`)}</p>
      </div>

      <Show when={can("metrics.read")}>
        <Show when={metricsQuery.isError}>
          <QueryErrorState
            testId="admin-overview-error"
            compact
            onRetry={() => void metricsQuery.refetch()}
          />
        </Show>
        <Show when={!metricsQuery.isError}>
          <Show
            when={!metricsQuery.isLoading}
            fallback={<Skeleton class="h-24 w-full rounded-xl" />}
          >
            <Show when={metricsQuery.isSuccess && metricsQuery.data}>
              {(m) => (
                <Card class="bg-primary/5 border-primary/10">
                  <CardContent class="p-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Stat label={t(msg`Users`)} value={m().totalUsers} />
                    <Stat label={t(msg`Active sessions`)} value={m().activeSessions} />
                    <Stat label={t(msg`Signups (7d)`)} value={m().newSignups7d} />
                    <Stat label={t(msg`Banned`)} value={m().bannedUsers} />
                  </CardContent>
                </Card>
              )}
            </Show>
          </Show>
        </Show>
      </Show>

      <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <For each={sections()}>
          {(section) => (
            <Link to={section.to} class="block">
              <Card class="transition-colors hover:border-primary/50 hover:bg-muted/40 h-full">
                <CardContent class="p-5 flex items-start gap-4">
                  <div class="h-10 w-10 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <section.icon class="h-5 w-5" />
                  </div>
                  <div class="min-w-0">
                    <div class="font-bold">{section.label}</div>
                    <div class="text-xs text-muted-foreground">{section.description}</div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}
        </For>
      </div>
    </div>
  );
}

function Stat(props: { label: string; value: number }) {
  return (
    <div class="space-y-1">
      <div class="text-xs text-muted-foreground">{props.label}</div>
      <div class="text-2xl font-bold tracking-tight">{props.value.toLocaleString()}</div>
    </div>
  );
}
